import React, { useMemo, useState } from 'react';
import { Check, Copy, ExternalLink, Share2 } from 'lucide-react';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogTrigger,
} from '@/components/ui/dialog';
import { useCampaignShareLink, useCreateShareLink, useRevokeShareLink } from '../../hooks/useShareLink';
import { toast } from '@/hooks/use-toast';

export interface ShareAnalyticsButtonProps {
  campaignId: string;
}

const formatExpiry = (expiresAt: string): string => {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(expiresAt));
};

export const ShareAnalyticsButton: React.FC<ShareAnalyticsButtonProps> = ({ campaignId }) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const { data: shareLink, isLoading, isError, refetch } = useCampaignShareLink(open ? campaignId : '');
  const createShareLink = useCreateShareLink();
  const revokeShareLink = useRevokeShareLink();

  const shareUrl = useMemo(() => {
    if (!shareLink?.token) return '';
    return `${window.location.origin}/shared/analytics/${shareLink.token}`;
  }, [shareLink?.token]);

  const handleCreate = () => {
    createShareLink.mutate(campaignId);
  };

  const handleCopy = async () => {
    if (!shareUrl) return;

    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast({
        title: 'Link copied',
        description: 'The public analytics link is ready to share.',
      });
      window.setTimeout(() => setCopied(false), 2_000);
    } catch (error) {
      console.error('Error copying share link:', error);
      toast({
        title: 'Could not copy link',
        description: 'Please copy the link manually.',
        variant: 'destructive',
      });
    }
  };

  const handleRevoke = () => {
    if (!shareLink?.token) return;
    revokeShareLink.mutate({ token: shareLink.token, campaignId });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          type="button"
          disabled={!campaignId}
        >
          <Share2 className="w-4 h-4" />
          <span>Share</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl p-8 rounded-3xl bg-white border border-slate-100">
        <div className="flex flex-col sm:flex-row justify-between items-start gap-3 border-b border-slate-100">
          <div className="flex gap-4 items-start flex-1">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#EEF2FF] text-[#5B2DF0]">
              <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 3v18h18" />
                <path d="m19 9-5 5-4-4-3 3" />
              </svg>
            </div>
            <div>
              <h2 className="text-[32px] font-medium text-slate-900 leading-snug font-['Playfair_Display',_serif]">
                Share public analytics
              </h2>
              <p className="mt-2 text-sm text-slate-500 leading-relaxed">
                Generate a read-only link to share this campaign's analytics. No budget or payment details will be exposed.
              </p>
            </div>
          </div>

          {/* Top right image */}
          <div className="w-[200px] h-[140px] shrink-0 relative flex items-center justify-center self-center sm:self-start">
            <img src="/images/ShareAnalytics.png" alt="Share Analytics" className="w-full h-full object-contain rotate-[3deg]" />
          </div>
        </div>

        <div className="space-y-6 pt-6">
          {isLoading && (
            <div className="flex items-center gap-2 rounded-2xl border border-slate-200 p-4 text-sm text-slate-600 bg-slate-50/50">
              <LoadingSpinner size="small" />
              Checking for an existing share link...
            </div>
          )}

          {isError && (
            <div className="rounded-2xl border border-red-200 bg-red-50/50 p-4 text-sm text-red-700">
              Could not load the existing share link.
              <button
                type="button"
                onClick={() => void refetch()}
                className="ml-2 font-medium underline"
              >
                Retry
              </button>
            </div>
          )}

          {!isLoading && !shareLink && (
            <div className="rounded-2xl border border-slate-150 bg-slate-50/50 p-6 text-center space-y-4 shadow-sm">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white text-slate-400 mx-auto border border-slate-100 shadow-sm">
                <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                  <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                </svg>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-slate-800">No active public link</h4>
                <p className="text-xs text-slate-500 mt-1 max-w-sm mx-auto">Create a secure, read-only analytics link to share this campaign's performance dashboard with external clients or team members.</p>
              </div>
              <Button
                type="button"
                onClick={handleCreate}
                disabled={createShareLink.isPending}
              >
                {createShareLink.isPending ? (
                  <LoadingSpinner size="small" iconClassName="text-white" />
                ) : (
                  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                )}
                Generate share link
              </Button>
            </div>
          )}

          {shareLink && (
            <div className="space-y-6">
              {/* Status Bar */}
              <div className="rounded-2xl border border-emerald-100 bg-[#F0FDF4] p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-600 text-white shadow-sm">
                    <Check className="h-6 w-6 stroke-[3px]" />
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-emerald-800">Link is active</h4>
                    <p className="text-xs text-emerald-600 mt-0.5">Anyone with this link can view the analytics.</p>
                  </div>
                </div>

                {/* Expiry Box */}
                <div className="flex items-center gap-2 bg-white border border-slate-100 rounded-xl px-3 py-1.5 shadow-sm">
                  <svg className="h-4 w-4 text-emerald-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                    <line x1="16" y1="2" x2="16" y2="6" />
                    <line x1="8" y1="2" x2="8" y2="6" />
                    <line x1="3" y1="10" x2="21" y2="10" />
                  </svg>
                  <div className="flex flex-col text-left">
                    <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Active Until</span>
                    <span className="text-xs font-semibold text-slate-800">{formatExpiry(shareLink.expires_at)}</span>
                  </div>
                </div>
              </div>

              {/* Public analytics link card */}
              <div className="rounded-2xl border border-slate-100 bg-white p-5 space-y-4 shadow-sm">
                <div className="space-y-2">
                  <label className="text-xs font-bold text-slate-800 uppercase tracking-wider">Public analytics link</label>
                  <div className="flex items-center gap-3">
                    <div className="relative flex-1">
                      <svg className="absolute left-3 top-3.5 h-4 w-4 text-slate-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                      </svg>
                      <input
                        type="text"
                        value={shareUrl}
                        readOnly
                        className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-600 focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
                      />
                    </div>
                    <Button
                      onClick={handleCopy}
                      className="shrink-0"
                    >
                      {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                      <span>Copy link</span>
                    </Button>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
                      <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                      </svg>
                    </div>
                    <div>
                      <h5 className="text-sm font-semibold text-slate-800">Secure & read-only</h5>
                      <p className="text-xs text-slate-500 mt-0.5">This link does not expose any sensitive information.</p>
                    </div>
                  </div>

                  <a
                    href={shareUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm font-semibold text-purple-600 hover:text-purple-700 transition-colors"
                  >
                    Open public view
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-end pt-4 border-t border-slate-100">
                <Button
                  onClick={handleRevoke}
                  disabled={revokeShareLink.isPending}
                  variant="destructive"
                >
                  {revokeShareLink.isPending ? (
                    <LoadingSpinner size="small" iconClassName="text-white" />
                  ) : (
                    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="3 6 5 6 21 6" />
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                    </svg>
                  )}
                  Revoke link
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ShareAnalyticsButton;
